// ============================================================================
// ELIGIBILITY CRITERIA FORM MODAL — Add / Edit / View (CFG-ELIG-001)
// ============================================================================
// Builder for a per-(case-type, product) actionable-account criteria set.
// Structured rows: a mandatory block (ALL/ANY) + an optional any-of block.
// Saves are versioned server-side. Follows the enterprise form-modal
// conventions (dirty tracking, save/discard confirm, loading overlay, minimize).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useCaseTypeOptions } from 'hooks/api/useCaseTypes';
import { useProducts } from 'hooks/api/useProducts';
import { useEligibilityFields } from 'hooks/api/useEligibilityCriteria';
import { eligibilityCriteriaService } from 'api/services/eligibilityCriteriaService';
import type {
  EligibilityCriteriaListItem,
  EligibilityField,
  SaveEligibilityCriteriaRequest,
  SaveEligibilityConditionRequest,
} from 'api/types/eligibilityCriteria.types';

interface EditCondition {
  key: string;
  fieldCode: string;
  operator: string;
  raw: string;
  groupNo: 0 | 1;
}

interface FormState {
  caseTypeCode: string;
  productId: string;        // '' = base (all products)
  matchMode: 'ALL' | 'ANY';
  effectiveFrom: string;
  effectiveTo: string;      // '' = open-ended (current)
  description: string;
  conditions: EditCondition[];
}

const today = () => new Date().toISOString().split('T')[0];
let keySeq = 0;
const nextKey = () => `c${++keySeq}`;

const INITIAL: FormState = {
  caseTypeCode: '', productId: '', matchMode: 'ALL', effectiveFrom: today(), effectiveTo: '', description: '', conditions: [],
};

export interface EligibilityCriteriaFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'view';
  record?: EligibilityCriteriaListItem | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'view', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const EligibilityCriteriaFormModal: React.FC<EligibilityCriteriaFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();
  const { options: caseTypeOptions } = useCaseTypeOptions();
  const { data: products } = useProducts({ pageSize: 100, sortBy: 'productCode', sortOrder: 'asc' });
  const { data: fields } = useEligibilityFields();

  const [formData, setFormData] = useState<FormState>(INITIAL);
  const formDataRef = useRef(formData); formDataRef.current = formData;
  const recordRef = useRef(record); recordRef.current = record;
  const initialSnapshotRef = useRef('');
  const modeRef = useRef(mode); modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const isRestoreRef = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fieldsByCode = useMemo(() => {
    const m: Record<string, EligibilityField> = {};
    fields.forEach(f => { m[f.fieldCode] = f; });
    return m;
  }, [fields]);

  const fieldOptions = useMemo(
    () => fields.map(f => ({ value: f.fieldCode, label: f.unit ? `${f.label} (${f.unit})` : f.label })),
    [fields],
  );
  const productOptions = useMemo(
    () => [{ value: '', label: 'All products (base)' }, ...products.map(p => ({ value: p.id, label: p.productCode }))],
    [products],
  );

  // Populate on open. Edit/View fetches the full set (the grid row has no conditions).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }

    const rec = recordRef.current;
    if (rec) {
      let cancelled = false;
      setIsLoading(true);
      eligibilityCriteriaService.getById(rec.id).then(set => {
        if (cancelled || !set) return;
        const data: FormState = {
          caseTypeCode: set.caseTypeCode,
          productId: set.productId ?? '',
          matchMode: set.matchMode,
          effectiveFrom: (set.effectiveFrom || today()).slice(0, 10),
          effectiveTo: set.effectiveTo ? set.effectiveTo.slice(0, 10) : '',
          description: set.description ?? '',
          conditions: set.conditions
            .slice()
            .sort((a, b) => a.groupNo - b.groupNo || a.displayOrder - b.displayOrder)
            .map(c => ({
              key: nextKey(),
              fieldCode: c.fieldCode,
              operator: c.operator,
              raw: toRaw(c),
              groupNo: (c.groupNo === 1 ? 1 : 0) as 0 | 1,
            })),
        };
        setFormData(data);
        initialSnapshotRef.current = JSON.stringify(data);
      }).finally(() => { if (!cancelled) setIsLoading(false); });
      return () => { cancelled = true; };
    }

    const fresh = { ...INITIAL, effectiveFrom: today(), conditions: [] as EditCondition[] };
    setFormData(fresh);
    initialSnapshotRef.current = JSON.stringify(fresh);
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const cur = formDataRef.current;
    const curMode = modeRef.current;
    const curInstance = createInstanceIdRef.current;
    const curRecord = recordRef.current;

    const modalId = curMode === 'create'
      ? `${tabId}-form-modal-create-${curInstance || 'default'}`
      : `${tabId}-form-modal-edit-${curRecord?.id || 'new'}`;

    const scopeLabel = cur.caseTypeCode || 'criteria';
    const title = curMode === 'create'
      ? `Add - ${scopeLabel} (${curInstance || '1'})`
      : `Edit - ${scopeLabel}`;

    const savedFormData = { ...cur };
    const savedSnapshot = initialSnapshotRef.current;
    const curIsDirty = savedSnapshot ? JSON.stringify(cur) !== savedSnapshot : false;

    minimize({
      id: modalId, title, moduleName, tabId, isDirty: curIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            curMode,
            curMode === 'create' ? curInstance : undefined,
            curMode !== 'create' ? curRecord?.id : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const addCondition = useCallback((groupNo: 0 | 1) => {
    setFormData(p => ({ ...p, conditions: [...p.conditions, { key: nextKey(), fieldCode: '', operator: '', raw: '', groupNo }] }));
  }, []);
  const updateCondition = useCallback((key: string, patch: Partial<EditCondition>) => {
    setFormData(p => ({ ...p, conditions: p.conditions.map(c => c.key === key ? { ...c, ...patch } : c) }));
  }, []);
  const removeCondition = useCallback((key: string) => {
    setFormData(p => ({ ...p, conditions: p.conditions.filter(c => c.key !== key) }));
  }, []);

  const handleSaveClick = useCallback(() => {
    if (!formData.caseTypeCode) { notificationService.toast.warning('Select a case type.'); return; }
    if (formData.effectiveTo && formData.effectiveFrom && formData.effectiveTo < formData.effectiveFrom) {
      notificationService.toast.warning('Effective To must be on or after Effective From.'); return;
    }
    if (formData.conditions.length === 0) { notificationService.toast.warning('Add at least one condition.'); return; }
    for (const c of formData.conditions) {
      if (!c.fieldCode || !c.operator) { notificationService.toast.warning('Every condition needs a field and operator.'); return; }
      if (c.raw.trim() === '') { notificationService.toast.warning('Every condition needs a value.'); return; }
    }
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const conditions: SaveEligibilityConditionRequest[] = formData.conditions.map((c, idx) => {
        const f = fieldsByCode[c.fieldCode];
        const base: SaveEligibilityConditionRequest = {
          fieldCode: c.fieldCode, operator: c.operator as any, groupNo: c.groupNo, displayOrder: idx,
        };
        if (!f) return base;
        if (f.dataType === 'BOOLEAN') base.valueNum = c.raw === 'true' ? 1 : 0;
        else if (f.dataType === 'ENUM') {
          if (c.operator === 'IN' || c.operator === 'NOT_IN') base.valueList = c.raw.split(',').map(s => s.trim()).filter(Boolean);
          else base.valueText = c.raw.trim();
        } else base.valueNum = Number(c.raw);
        return base;
      });

      const payload: SaveEligibilityCriteriaRequest = {
        caseTypeCode: formData.caseTypeCode,
        productId: formData.productId || null,
        matchMode: formData.matchMode,
        effectiveFrom: formData.effectiveFrom,
        effectiveTo: formData.effectiveTo || null,
        description: formData.description || null,
        conditions,
      };

      await eligibilityCriteriaService.save(payload);
      setIsSaving(false);
      notificationService.toast.success('Eligibility criteria saved.');
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const msg = err?.response?.data?.message
        || (err?.response?.data?.errors?.length ? err.response.data.errors.join(' ') : null)
        || 'Failed to save eligibility criteria.';
      notificationService.toast.error(msg, 6000);
    }
  }, [formData, fieldsByCode, onSaved, onClose]);

  if (!isOpen) return null;

  const isView = mode === 'view';
  const group0 = formData.conditions.filter(c => c.groupNo === 0);
  const group1 = formData.conditions.filter(c => c.groupNo === 1);

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {(isSaving || isLoading) && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {isSaving ? 'Saving…' : 'Loading…'}
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {isView ? 'View Eligibility Criteria' : mode === 'create' ? 'Add Eligibility Criteria' : 'Edit Eligibility Criteria'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <fieldset disabled={isView} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
            <div className="form-grid-2">
              <div className="form-group" data-field="caseTypeCode">
                <SyutSelect
                  label={<>Case Type <span className="text-danger">*</span></>}
                  options={caseTypeOptions}
                  value={formData.caseTypeCode}
                  onChange={(v) => setFormData(p => ({ ...p, caseTypeCode: (v as string) || '' }))}
                  isClearable={false}
                  isDisabled={isView || mode === 'edit'}  // scope is fixed once created; edit versions the same scope
                  placeholder="Select workflow…"
                />
              </div>
              <div className="form-group" data-field="productId">
                <SyutSelect
                  label="Product Scope"
                  options={productOptions}
                  value={formData.productId}
                  onChange={(v) => setFormData(p => ({ ...p, productId: (v as string) || '' }))}
                  isClearable={false}
                  isDisabled={isView || mode === 'edit'}
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-group" data-field="effectiveFrom">
                <label className="form-label">Effective From <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={formData.effectiveFrom}
                  onChange={(e) => setFormData(p => ({ ...p, effectiveFrom: e.target.value }))} disabled={isSaving} />
              </div>
              <div className="form-group" data-field="effectiveTo">
                <label className="form-label">Effective To</label>
                <input type="date" className="form-input" value={formData.effectiveTo}
                  min={formData.effectiveFrom || undefined}
                  onChange={(e) => setFormData(p => ({ ...p, effectiveTo: e.target.value }))} disabled={isSaving} />
                <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                  Leave blank for open-ended. Set a date to end this criteria.
                </div>
              </div>
            </div>

            <ConditionGroup
              title="Mandatory conditions"
              matchMode={formData.matchMode}
              onMatchModeChange={(m) => setFormData(p => ({ ...p, matchMode: m }))}
              conditions={group0} fieldsByCode={fieldsByCode} fieldOptions={fieldOptions} readOnly={isView}
              onAdd={() => addCondition(0)} onUpdate={updateCondition} onRemove={removeCondition}
            />
            <ConditionGroup
              title="Any-of conditions (optional) — at least one must match"
              conditions={group1} fieldsByCode={fieldsByCode} fieldOptions={fieldOptions} readOnly={isView}
              onAdd={() => addCondition(1)} onUpdate={updateCondition} onRemove={removeCondition}
            />

            <div className="form-group" data-field="description" style={{ marginTop: '0.75rem' }}>
              <label className="form-label">Notes (optional)</label>
              <textarea className="form-input" rows={2} value={formData.description}
                onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Why these criteria / what changed in this version…" disabled={isSaving} />
            </div>
          </fieldset>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>{isView ? 'Close' : 'Cancel'}</button>
          {!isView && (
            <button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
              {mode === 'create' ? 'Save' : 'Update'}
            </button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Save eligibility criteria?"
        message="Saving creates a new effective-dated version; the previous version is retained in history. This does not retroactively remove accounts already added to a litigation."
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

// ----------------------------------------------------------------------------

function toRaw(c: { dataType: string; operator: string; valueNum?: number | null; valueText?: string | null; valueList?: string[] | null }): string {
  if (c.dataType === 'BOOLEAN') return c.valueNum === 1 ? 'true' : 'false';
  if (c.dataType === 'ENUM') {
    if (c.operator === 'IN' || c.operator === 'NOT_IN') return (c.valueList ?? []).join(', ');
    return c.valueText ?? '';
  }
  return c.valueNum == null ? '' : String(c.valueNum);
}

interface ConditionGroupProps {
  title: string;
  /** When provided, an inline ALL/ANY toggle is shown after the title (group-0 only). */
  matchMode?: 'ALL' | 'ANY';
  onMatchModeChange?: (m: 'ALL' | 'ANY') => void;
  conditions: EditCondition[];
  fieldsByCode: Record<string, EligibilityField>;
  fieldOptions: { value: string; label: string }[];
  readOnly: boolean;
  onAdd: () => void;
  onUpdate: (key: string, patch: Partial<EditCondition>) => void;
  onRemove: (key: string) => void;
}

const MatchModeToggle: React.FC<{ mode: 'ALL' | 'ANY'; onChange: (m: 'ALL' | 'ANY') => void; disabled: boolean }> = ({ mode, onChange, disabled }) => {
  if (disabled) {
    return <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
      — {mode === 'ALL' ? 'all must match' : 'any must match'}
    </span>;
  }
  const opts: Array<{ v: 'ALL' | 'ANY'; label: string }> = [
    { v: 'ALL', label: 'all must match' },
    { v: 'ANY', label: 'any must match' },
  ];
  return (
    <span style={{ display: 'inline-flex', border: '1px solid var(--form-border-color)', borderRadius: 999, overflow: 'hidden', marginLeft: '0.5rem' }}>
      {opts.map(o => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          title={o.v === 'ALL' ? 'AND — every mandatory condition must be true' : 'OR — at least one mandatory condition must be true'}
          style={{
            padding: '0.1rem 0.6rem', fontSize: '0.72rem', fontWeight: 600, border: 'none', cursor: 'pointer',
            background: mode === o.v ? '#2563eb' : 'transparent',
            color: mode === o.v ? '#fff' : 'var(--text-secondary)',
          }}
        >{o.label}</button>
      ))}
    </span>
  );
};

const ConditionGroup: React.FC<ConditionGroupProps> = ({
  title, matchMode, onMatchModeChange, conditions, fieldsByCode, fieldOptions, readOnly, onAdd, onUpdate, onRemove,
}) => (
  <div style={{ marginBottom: '0.85rem' }}>
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      borderBottom: '1px solid var(--border-color)', paddingBottom: '0.3rem', marginBottom: '0.5rem',
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)' }}>{title}</span>
        {matchMode && onMatchModeChange && (
          <MatchModeToggle mode={matchMode} onChange={onMatchModeChange} disabled={readOnly} />
        )}
      </span>
      {!readOnly && (
        <button type="button" className="btn btn-secondary" style={{ padding: '0.25rem 0.65rem', fontSize: '0.78rem' }} onClick={onAdd}>
          <IconSystem.add size={12} style={{ marginRight: '0.3rem' }} /> Add condition
        </button>
      )}
    </div>

    {conditions.length === 0 ? (
      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic', padding: '0.3rem 0' }}>
        No conditions in this group.
      </div>
    ) : (
      conditions.map(c => {
        const field = fieldsByCode[c.fieldCode];
        const opOptions = (field?.allowedOperators ?? []).map(o => ({ value: o, label: o }));
        const isBool = field?.dataType === 'BOOLEAN';
        const isEnum = field?.dataType === 'ENUM';
        const isList = c.operator === 'IN' || c.operator === 'NOT_IN';
        return (
          <div key={c.key} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.45rem' }}>
            <div style={{ flex: '0 0 210px' }}>
              <SyutSelect options={fieldOptions} value={c.fieldCode} size="sm" placeholder="Field…"
                isDisabled={readOnly}
                onChange={(v) => onUpdate(c.key, { fieldCode: (v as string) || '', operator: '', raw: '' })} />
            </div>
            <div style={{ flex: '0 0 110px' }}>
              <SyutSelect options={opOptions} value={c.operator} size="sm" placeholder="Op…"
                isDisabled={readOnly || !c.fieldCode}
                onChange={(v) => onUpdate(c.key, { operator: (v as string) || '', raw: '' })} />
            </div>
            <div style={{ flex: 1, minWidth: 130 }}>
              {isBool ? (
                <SyutSelect options={[{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }]}
                  value={c.raw || ''} size="sm" isClearable={false} isDisabled={readOnly || !c.operator}
                  onChange={(v) => onUpdate(c.key, { raw: (v as string) || '' })} />
              ) : (
                <input type={isEnum ? 'text' : 'number'} className="form-input" value={c.raw}
                  disabled={readOnly || !c.operator}
                  placeholder={isEnum ? (isList ? 'value1, value2, …' : 'value') : 'number'}
                  onChange={(e) => onUpdate(c.key, { raw: e.target.value })}
                  style={{ fontSize: '0.82rem', padding: '0.375rem 0.625rem' }} />
              )}
            </div>
            {!readOnly && (
              <button type="button" onClick={() => onRemove(c.key)} title="Remove condition"
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#dc2626', padding: '0.25rem' }}>
                <IconSystem.trash size={14} />
              </button>
            )}
          </div>
        );
      })
    )}
  </div>
);

export default EligibilityCriteriaFormModal;
